import React, { Fragment, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Popup } from 'src/components/Popup';
import FormRow from 'src/components/Form/FormRow';
import { useEntityUsages, useEntityUsedInFutureJobs } from 'src/hooks/entityUsage';
import Loader from 'src/components/Loader';
import { useTranslation } from 'react-i18next';
import s from './EntityDeleteConfirm.styles';

const EntityDeleteConfirm = props => {
  const { onSubmit, onClose, resourceName, id } = props;

  const { t } = useTranslation();

  const { data, loading } = useEntityUsages({
    resourceName,
    id: +id,
    skip: [
      'User',
      'Role',
      'FilteringRuleGroup',
      'ReportTemplate',
      'EmailTemplate',
      'Notification',
      'CapAction',
    ].includes(resourceName),
  });
  const entityUsagesData = (data || {}).entityUsages || { entityUsages: [] };
  const { entityUsages } = entityUsagesData;

  let usedInFutureJobsParams = { skip: true };
  switch (resourceName) {
  case 'Company':
    usedInFutureJobsParams = {
      companyIds: [+id],
      skip: false,
    };
    break;
  case 'User':
    usedInFutureJobsParams = {
      userIds: [+id],
      skip: false,
    };
    break;
  case 'Service':
    usedInFutureJobsParams = {
      serviceIds: [+id],
      skip: false,
    };
    break;
  case 'Checklist':
  case 'ChecklistQuestion':
  case 'Datasheet':
  case 'DatasheetQuestion':
  case 'ResultFamily':
    usedInFutureJobsParams = {
      serviceIds: [],
      skip: false,
    };
    break;
  default:
    break;
  }
  const { data: entityUsedInFutureJobsData, loading: entityUsedInFutureJobsLoading, refetch } = useEntityUsedInFutureJobs(usedInFutureJobsParams);

  const serviceIds = entityUsages.filter(c => c.entity === 'Service').map(c => c.entityId);
  useEffect(() => {
    if (serviceIds.length === 0) {
      return;
    }

    refetch({ serviceIds });
  }, [serviceIds.join(',')]);

  let content = null;
  if (!loading && !entityUsedInFutureJobsLoading) {
    if (entityUsedInFutureJobsData?.entityUsedInFutureJobs) {
      content = (
        <s.Text>{t('DELETE_CONFIRM_USED_IN_FUTURE_JOBS')}</s.Text>
      );
    } else {
      content =
        entityUsages.length === 0 ? (
          <s.Text>{t('DELETE_CONFIRM')}</s.Text>
        ) : (
          <Fragment>
            <s.Text>{t('DELETE_CONFIRM_USAGES')}</s.Text>
            <s.EntityUsages>
              {entityUsages.map(({ name }, index) => (
                <div key={index}>{name}</div>
              ))}
            </s.EntityUsages>
          </Fragment>
        );
    }
  }

  return (
    <Popup
      onClose={onClose}
      title={<s.Title>{t('DELETE_CONFIRM_TITLE')}</s.Title>}
      withOverflow
      >
      {(loading || entityUsedInFutureJobsLoading) && <Loader />}
      {content}
      {!loading && !entityUsedInFutureJobsLoading && !entityUsedInFutureJobsData?.entityUsedInFutureJobs && (
        <FormRow inline spaced>
          <s.SubmitButton
            label={t('YES')} 
            onClick={e => {
              e.preventDefault();
              onSubmit(null);
              onClose();
            }}
          />
          <s.LinkButton 
            variant='btn--outline-link' 
            label={t('NO')} 
            onClick={onClose}
          />
        </FormRow>
      )}
    </Popup>
  );
};

EntityDeleteConfirm.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  resourceName: PropTypes.string.isRequired,
  id: PropTypes.number.isRequired,
};

EntityDeleteConfirm.defaultProps = {};

export default EntityDeleteConfirm;
